import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { Bot, ChevronRight, ListTodo, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";

function Mini({ icon: Icon, value, label, tone = "muted" }) {
  const toneCls = {
    muted: "text-foreground",
    warn: "text-amber-600",
    good: "text-primary",
  }[tone];
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-background/70 px-3 py-2">
      <Icon className={`h-4 w-4 shrink-0 ${toneCls}`} />
      <div className="leading-tight">
        <p className={`font-display text-lg font-semibold ${toneCls}`}>{value}</p>
        <p className="text-[11px] text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

const STATUS_KEY = { trialing: "aipa.status_trial", active: "aipa.status_active", canceled: "aipa.status_canceled" };

export default function AssistantDashboardCard() {
  const t = useT();
  const [sum, setSum] = useState(null);

  useEffect(() => {
    api.get("/coach/assistant/summary").then(({ data }) => setSum(data)).catch(() => setSum({ active: false }));
  }, []);

  if (!sum) {
    return <div className="h-28 animate-pulse rounded-3xl border border-border bg-card" data-testid="coach-aipa-card-loading" />;
  }

  const active = sum.active && sum.setup_done;
  const badge = STATUS_KEY[sum.status] ? t(STATUS_KEY[sum.status]) : null;

  return (
    <Link
      to="/app/coach/assistant"
      data-testid="coach-aipa-card"
      className="group block overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-6 transition-colors hover:from-primary/15"
    >
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary">
          <Bot className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display text-xl text-foreground">{t("aipa.title")}</h2>
            {badge && (
              <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary" data-testid="coach-aipa-status">
                {badge}
              </span>
            )}
          </div>

          {active ? (
            <>
              <p className="mt-1 text-sm text-muted-foreground" data-testid="coach-aipa-summary">
                {sum.clients_summary
                  ? sum.clients_summary
                  : t("aipa.card_ready")}
              </p>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Mini icon={ListTodo} value={sum.open_tasks ?? 0} label={t("aipa.card_open_tasks")} />
                <Mini icon={AlertTriangle} value={sum.attention_count ?? 0} label={t("aipa.card_needs_attention")} tone={(sum.attention_count ?? 0) > 0 ? "warn" : "muted"} />
                <Mini icon={CheckCircle2} value={sum.pending_workflow ?? 0} label={t("aipa.card_to_approve")} tone={(sum.pending_workflow ?? 0) > 0 ? "good" : "muted"} />
              </div>
            </>
          ) : sum.active && !sum.setup_done ? (
            <p className="mt-1 text-sm text-muted-foreground">
              {t("aipa.card_setup_step")}
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("aipa.card_pitch")}
              </p>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground">
                <Sparkles className="h-4 w-4" /> {sum.trial_used ? t("aipa.start_sub") : t("aipa.card_try_free")}
              </span>
            </>
          )}
        </div>
        <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}
